import { FC, useEffect, useState } from 'react';

import { Button, Col, Row } from 'antd';

import Card from '@/components/RCard';
import { CreateReportSteps } from '@/constants';
import { SimulationResult } from '@/types/simulations';

import CreateReport from './CreateReportStep/Step3';
import SelectMetric from './SelectMetricStep/Step2';
import SelectSimulation from './SelectSimulationStep/Step1';
import StyledCol from './styles';
import RDrawer from '../RDrawer';
import CustomSteps from '../RSteps';

interface RSimReportProps {
  isOpen: boolean;
  onClose: () => void;
}

const RSimReport: FC<RSimReportProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedSimulations, setSelectedSimulations] = useState<
    SimulationResult[]
  >([]);
  const [selectedMetric, setSelectedMetric] = useState<string>('');
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<string>('');
  const [reportName, setReportName] = useState<string>('');
  const [reportTitle, setReportTitle] = useState<string>('');

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0);
      setSelectedSimulations([]);
      setSelectedMetric('');
      setSelectedTimeFrame('');
      setReportName('');
      setReportTitle('');
    }
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return selectedSimulations.length > 0;
      case 1:
        return selectedMetric && selectedTimeFrame;
      case 2:
        return reportName.trim() !== '';
      default:
        return false;
    }
  };

  const steps = [
    {
      title: 'Select Simulations',
      content: (
        <SelectSimulation
          selectedSimulations={selectedSimulations}
          onSelectedSimulationsChange={setSelectedSimulations}
        />
      ),
    },
    {
      title: 'Select Metric',
      content: (
        <SelectMetric
          selectedSimulations={selectedSimulations}
          selectedMetric={selectedMetric}
          selectedTimeFrame={selectedTimeFrame}
          onMetricChange={setSelectedMetric}
          onTimeFrameChange={setSelectedTimeFrame}
        />
      ),
    },
    {
      title: 'Create Report',
      content: (
        <CreateReport
          selectedSimulations={selectedSimulations}
          selectedMetric={selectedMetric}
          selectedTimeFrame={selectedTimeFrame}
          reportName={reportName}
          onReportNameChange={setReportName}
          reportTitle={reportTitle}
          onReportTitleChange={setReportTitle}
        />
      ),
    },
  ];

  return (
    <RDrawer
      title="Create Simulation Report"
      description="Create a simulation report by selecting the completed simulations and setting the report parameters."
      placement="right"
      onClose={onClose}
      open={isOpen}
      destroyOnClose
      footer={false}
    >
      <StyledCol>
        <Card className="pt-16 px-16 pb-12 content-container mt-24">
          <Col className="steps">
            <CustomSteps current={currentStep} items={steps} type="default" />
          </Col>

          <Card className="py-24 px-24 current-card mt-24">
            {steps[currentStep].content}
          </Card>
          <Row
            className="bottom-button-group"
            align="middle"
            justify="space-between"
          >
            <Button className="btn-cancel" onClick={() => onClose()}>
              Cancel
            </Button>
            <div className="right-side-btn-group">
              {currentStep > CreateReportSteps.SELECT_SIMULATIONS && (
                <Button
                  className="btn-back"
                  type="default"
                  onClick={() => handlePrev()}
                >
                  Previous
                </Button>
              )}

              {currentStep < steps.length - 1 && (
                <Button
                  className="btn-next"
                  type="primary"
                  onClick={() => handleNext()}
                  disabled={!canProceed()}
                >
                  Next
                </Button>
              )}
              {currentStep === steps.length - 1 && (
                <Button
                  type="primary"
                  className="btn-next"
                  onClick={() => onClose()}
                >
                  Finish
                </Button>
              )}
            </div>
          </Row>
        </Card>
      </StyledCol>
    </RDrawer>
  );
};

export default RSimReport;
